import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';
import type {
  ArgoAppsResult,
  BlacklistEntry,
  DeployedNodeVersionsResult,
  InventoryRepo,
  InventoryStats,
  NodeInventoryApi,
  NodeScheduleResponse,
} from './types';

export class NodeInventoryClient implements NodeInventoryApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('node-inventory');
  }

  private async fetch<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${await this.baseUrl()}${path}`;
    const res = await this.fetchApi.fetch(url, init);
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return res.json() as Promise<T>;
  }

  async getInventory(): Promise<{
    stats: InventoryStats;
    repos: InventoryRepo[];
  }> {
    return this.fetch('/inventory');
  }

  async getNodeSchedule(): Promise<NodeScheduleResponse> {
    return this.fetch('/node-schedule');
  }

  async getBlacklist(): Promise<{ blacklist: BlacklistEntry[] }> {
    return this.fetch('/blacklist');
  }

  async addToBlacklist(repo: string, reason: string): Promise<void> {
    await this.fetch('/blacklist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ repo, reason }),
    });
  }

  async removeFromBlacklist(repo: string): Promise<void> {
    await this.fetch(`/blacklist/${encodeURIComponent(repo)}`, {
      method: 'DELETE',
    });
  }

  async triggerScan(): Promise<void> {
    await this.fetch('/scan', { method: 'POST' });
  }

  async triggerArgoRefresh(): Promise<void> {
    await this.fetch('/argo-apps/refresh', { method: 'POST' });
  }

  async triggerDeployedNodeRefresh(): Promise<void> {
    await this.fetch('/deployed-node-versions/refresh', { method: 'POST' });
  }

  // Reads pre-fetched rows from the node-inventory database. The backend
  // refreshes them on a schedule from the Make Datastore.
  async getDeployedNodeVersions(): Promise<DeployedNodeVersionsResult> {
    return this.fetch<DeployedNodeVersionsResult>('/deployed-node-versions');
  }

  // Reads pre-fetched rows from the node-inventory database. The backend
  // refreshes them on a schedule from the deployments plugin.
  async getArgoApps(): Promise<ArgoAppsResult> {
    return this.fetch<ArgoAppsResult>('/argo-apps');
  }
}
